import getConfig from 'next/config'
import Head from 'next/head'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { serverSideConfigs, t } from 'localization'
import { serverRequestService } from 'server/services/serverRequestService'
import { createMosaicClient } from 'server/utils/mosaicClient'
import { PagesQA } from 'shared/dataAttributes'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { useWindowSize } from 'shared/hooks/useWindowSize'
import { colors, fonts, media, styles } from 'shared/lib'
import { mediaQueries } from 'shared/theme'
import { Store } from 'shared/types/Store'
import { FCGetServerSidePropsContext, getServerPageProps } from 'shared/utils/getServerSideProps'
import { getStoreImageUrl } from 'shared/utils/store-utils'
import { FlightClubStore } from 'stores/components/FlightClubStore'
import FlightClubStoreBreadcrumbs from 'stores/components/FlightClubStoreBreadcrumbs'
import { FlightClubStorePanel } from 'stores/components/FlightClubStorePanel'

const TP = 'pages.storelocator'

type StoresPageProps = {
  stores: Store[]
  storeImages: { [key: string]: string }
}

const StoresPage = ({ stores, storeImages }: StoresPageProps) => {
  const [flightClubStores, setFlightClubStores] = useState<Store[]>(stores || [])
  const { isMobile } = useWindowSize()
  const isTempStorePagesRedesignedEnabled = useFeatureFlag(
    FeatureFlag.TEMP_WEB_FC_ENABLE_STORE_PAGE_REDESIGNED,
  )
  useEffect(() => {
    if (stores) {
      const sorted = [...stores].sort((a: Store, b: Store) => a?.id - b?.id)

      setFlightClubStores(sorted)
    }
  }, [stores])

  return (
    <>
      <Head>
        {isTempStorePagesRedesignedEnabled && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'http://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                  {
                    '@type': 'ListItem',
                    position: 1,
                    item: {
                      '@id': 'https://www.flightclub.com',
                      name: 'Flight Club',
                    },
                  },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    item: {
                      '@id': 'https://www.flightclub.com/storelocator',
                      name: 'Flight Club Stores',
                    },
                  },
                ],
              }),
            }}
          />
        )}
        <title>{t(`${TP}.pageTitle`, 'Stores | Flight Club')}</title>
      </Head>
      <Layout>
        <Wrapper $isTempStorePagesRedesignedEnabled={isTempStorePagesRedesignedEnabled}>
          {isTempStorePagesRedesignedEnabled ? (
            <>
              <DesktopBreadcrumbsWrapper>
                <FlightClubStoreBreadcrumbs />
              </DesktopBreadcrumbsWrapper>
              <GridHeader>
                <SubHeader>
                  <Title data-qa={PagesQA.StoresPageTitle}>
                    <div>{t(`${TP}.flightclub`, 'Flight Club')}</div>
                    <div>{t(`${TP}.stores`, 'Stores')}</div>
                  </Title>
                </SubHeader>
              </GridHeader>
            </>
          ) : (
            <Header data-qa={PagesQA.StoresPageTitle}>{t(`${TP}.stores`, 'Stores')}</Header>
          )}
          <Body $isTempStorePagesRedesignedEnabled={isTempStorePagesRedesignedEnabled}>
            {flightClubStores.map((store) =>
              isTempStorePagesRedesignedEnabled ? (
                <FlightClubStorePanel
                  key={store.id}
                  {...store}
                  image={getStoreImageUrl(store.id, isMobile, storeImages)}
                />
              ) : (
                <FlightClubStore key={store.id} {...store} />
              ),
            )}
          </Body>
        </Wrapper>
        {isTempStorePagesRedesignedEnabled && (
          <MobileBreadcrumbsWrapper>
            <FlightClubStoreBreadcrumbs />
          </MobileBreadcrumbsWrapper>
        )}
      </Layout>
    </>
  )
}

export const getServerSideProps = async (context: FCGetServerSidePropsContext) => {
  const { res, req, locale } = context
  try {
    const { serverRuntimeConfig } = getConfig()
    const { mosaicRaffleNamespace } = serverRuntimeConfig
    const mosaicClient = createMosaicClient(req, locale)
    const { data: storeImagesData } = await mosaicClient.get(
      `/api/v1/mosaic/${mosaicRaffleNamespace}/store-images`,
    )

    const { data } = await serverRequestService(req, res, 'sneakers').get(
      '/api/v1/flightclub_stores',
    )

    return {
      props: {
        ...(await getServerPageProps(context)),
        ...(await serverSideConfigs(res, locale)),
        stores: data.flightclubStores,
        storeImages: storeImagesData.images,
      },
    }
  } catch (error) {
    return {
      props: {
        ...(await getServerPageProps(context)),
        ...(await serverSideConfigs(res, locale)),
        notFound: true,
      },
    }
  }
}

const Layout = styled.div`
  background-color: ${colors.FC2_WHITE};
  display: grid;
`

const Wrapper = styled.div<{ $isTempStorePagesRedesignedEnabled: boolean }>`
  height: 100%;
  padding: ${styles.mobilePageLayoutPadding};
  ${media.large`
    max-width: 1500px;
    place-self: center;
    width: 100%;
    padding: ${({ $isTempStorePagesRedesignedEnabled }) =>
      $isTempStorePagesRedesignedEnabled ? '40px 150px 100px' : '80px 150px 100px'};
  `}
`
const Header = styled.h1`
  ${fonts.HEADER_1}
  margin-top: 0;
`

const GridHeader = styled.div`
  margin-top: 28px;
  display: grid;
  grid-gap: 24px;
  grid-template-columns: 1fr;
  ${media.large`
    margin-top: 40px;
    grid-template-columns: 1fr 1fr;
  `}
`

const SubHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
`

const Title = styled.h1`
  ${fonts.HEADER_2}
  font-weight: 500;
  font-size: 24px;
  margin: 0;
  width: 100%;
  line-height: 90%;
  text-align: left;
  ${media.large`
    font-size: 32px;
  `}
`

const MobileBreadcrumbsWrapper = styled.div`
  background-color: ${colors.FC2_WHITE};
  border-top: 1px solid ${colors.FC2_LIGHTEST_GREY};
  width: 100%;
  padding: ${styles.mobilePageLayoutPadding};
  margin: 0 20px 0 0;
  display: flex;
  flex-direction: row;
  place-content: center flex-start;
  overflow-wrap: break-word;
  ${mediaQueries.desktop} {
    display: none;
  }
`

const DesktopBreadcrumbsWrapper = styled.div`
  display: none;
  ${mediaQueries.desktop} {
    padding: 0;
    margin: 0 20px 0 0;
    display: flex;
    flex-direction: row;
    place-content: center flex-start;
    overflow-wrap: break-word;
    max-width: 600px;
  }
`

const Body = styled.div<{ $isTempStorePagesRedesignedEnabled: boolean }>`
  display: grid;
  ${({ $isTempStorePagesRedesignedEnabled }) =>
    $isTempStorePagesRedesignedEnabled
      ? `
      margin: 28px 0px 20px;
      row-gap: 4px;
      column-gap: 20px;
    `
      : `
      grid-gap: 15px
    `};
  grid-template-columns: 1fr;
  ${media.large`
    grid-template-columns: 1fr 1fr;
    margin: ${({ $isTempStorePagesRedesignedEnabled }) =>
      $isTempStorePagesRedesignedEnabled ? '20px 0px' : '0px'};
  `}
`

export default StoresPage
